import { OperationVariables, useLazyQuery } from "@apollo/client";
import { PaginatedList } from "@towersystems/roam-common/lib/shared-types";
import { omit } from "lodash";
import { useEffect, useState } from "react";
import { UseLoadMoreListOptions } from "./types";

const CONTROLLED_FILTER_KEYS = ["limit", "page"];

export const useLoadMoreList = <T>({
  query,
  resultKey,
  fixedVariables,
  variableFormatter,
  sort: optionsSort = {},
  initialLoad,
}: UseLoadMoreListOptions) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ [key: string]: any }>(optionsSort || {});
  const [limit] = useState(14);
  const [totalItems, setTotalItems] = useState(0);

  const [q] = useLazyQuery<{
    [key: string]: PaginatedList<T>;
  }>(query, {
    notifyOnNetworkStatusChange: true,
  });

  const loadMore = (variables?: OperationVariables, shouldReset?: boolean) => {
    const pageLoading = shouldReset ? 1 : page + 1;

    if (
      shouldReset === false &&
      items.length >= 1 &&
      items.length >= totalItems
    ) {
      return;
    }

    setLoading(true);
    const v = {
      limit,
      page: pageLoading,
      sort,
      ...omit<any, any>(fixedVariables, CONTROLLED_FILTER_KEYS),
      ...omit<any, any>(variables, CONTROLLED_FILTER_KEYS),
    };

    q({
      variables: variableFormatter ? variableFormatter(v) : v,
    })
      .then((res) => {
        setPage(pageLoading);
        const result = res.data && res.data[resultKey];
        if (result) {
          setTotalItems(result.meta.totalItems);
          setItems((prev) => {
            if (shouldReset) {
              return result.items;
            }
            return [...prev, ...result.items];
          });
        }
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (initialLoad) {
      loadMore();
    }
  }, []);

  const reset = () => {
    setPage(0);
    setTotalItems(0);
    setItems([]);
  };

  return {
    items,
    loadMore,
    reset,
    setSort,
    loading,
  };
};
